import Link                                                      from "next/link";
import { Crown, Medal, Award, ExternalLink, Skull }              from "lucide-react";
import Avatar                                                    from "@/components/Avatar";
import PatenteBadge                                              from "@/components/PatenteBadge";
import { ReisCoroas }                                            from "@/components/ReisDoJogador";
import { formatar_numero, formatar_valor, rotulo_da_unidade,
  texto_posicao, link_do_jogador, cor_segura, com_alfa }         from "@/utils/RpgFunction";

/* ===============================
   🏆 RANK ICON
================================ */
const getRankDisplay = (rank) => {
  if (rank === 1) {
    return (
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-cosmic-gold/20 mx-auto">
        <Crown className="w-5 h-5 text-cosmic-gold" />
      </div>
    );
  }

  if (rank === 2) {
    return (
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-400/20 mx-auto">
        <Medal className="w-5 h-5 text-gray-300" />
      </div>
    );
  }

  if (rank === 3) {
    return (
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-600/20 mx-auto">
        <Award className="w-5 h-5 text-amber-500" />
      </div>
    );
  }

  return (
    <span className="font-orbitron text-lg text-muted-foreground">
      {texto_posicao(rank)}
    </span>
  );
};

const valorClasse = (rank) => (
  rank === 1 ? "text-cosmic-gold text-neon-gold" : (typeof rank === "number" && rank <= 3) ? "text-primary" : "text-foreground"
);

/* ===============================
   📊 TABLE
   linhas: [{ jogador, posicao, valor }]
   categoria: null no Global (nivel e XP) ou a categoria do catalogo
   donoId: discordId de quem segura o Rei da categoria
================================ */
const RankingTable = ({ linhas = [], categoria = null, donoId = null, titulo, vazio = "Nenhum jogador encontrado." }) => {
  const global = !categoria;
  const cor    = global ? null : cor_segura(categoria.cor);

  return (
    <div className="void-card overflow-hidden">
      <div className="p-4 border-b border-border flex items-center gap-2">
        {!global && <Crown className="w-5 h-5" style={{ color: cor }} />}
        <h3 className="font-orbitron font-bold text-lg text-primary uppercase tracking-wider">
          {titulo || (global ? "Global" : categoria.nome)}
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="neon-table">
          <thead>
            <tr>
              <th className="w-16 table_center">Posição</th>
              <th>Jogador</th>
              <th className="hidden md:table-cell">Crew</th>
              {global ? (
                <>
                  <th className="text-center table_center">Nível</th>
                  <th className="hidden sm:table-cell text-center table_center">XP</th>
                </>
              ) : (
                <>
                  <th className="hidden sm:table-cell text-center table_center">Nível</th>
                  <th className="text-center table_center">{rotulo_da_unidade(categoria.unidade)}</th>
                </>
              )}
              <th className="w-12"></th>
            </tr>
          </thead>

          <tbody>
            {linhas.map(({ jogador, posicao, valor }, idx) => {
              const href    = link_do_jogador(jogador);
              const podio   = typeof posicao === "number" && posicao <= 3;
              const eDono   = !global && donoId && jogador?.discordId === donoId;

              return (
                <tr
                  key={jogador?.discordId || jogador?.slug || idx}
                  className={podio ? "bg-primary/5" : ""}
                >
                  <td className="text-center">
                    {getRankDisplay(posicao)}
                  </td>

                  <td>
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden border ${ posicao === 1 ? "border-cosmic-gold" : posicao === 2 ? "border-gray-400" : posicao === 3 ? "border-amber-600" : "border-border" }` }>
                        <Avatar
                          src={jogador?.avatar}
                          alt={jogador?.nome}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          {href ? (
                            <Link href={href} className="font-rajdhani font-semibold truncate hover:text-primary transition-colors">
                              {jogador?.nome}
                            </Link>
                          ) : (
                            <span className="font-rajdhani font-semibold truncate">{jogador?.nome}</span>
                          )}
                          {global && <ReisCoroas reis={jogador?.reis} />}
                          {eDono && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-orbitron font-bold uppercase flex-shrink-0"
                              style={{ color: cor, backgroundColor: com_alfa(cor, 0.15) }}
                            >
                              <Crown className="w-3 h-3" /> Rei
                            </span>
                          )}
                        </div>
                        <PatenteBadge patente={jogador?.patente} className="mt-1" />
                      </div>
                    </div>
                  </td>

                  <td className="hidden md:table-cell text-muted-foreground">
                    <div className="icon_crew_player"><Skull className="w-5 h-5" /> {jogador?.crew?.nome || "—"}</div>
                  </td>

                  {global ? (
                    <>
                      <td className="text-center">
                        <span className={`font-orbitron font-bold ${valorClasse(posicao)}`}>
                          {formatar_numero(jogador?.nivel)}
                        </span>
                      </td>
                      <td className="hidden sm:table-cell text-center text-muted-foreground font-rajdhani font-semibold">
                        {formatar_numero(jogador?.xp)}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="hidden sm:table-cell text-center text-muted-foreground font-rajdhani font-semibold">
                        {formatar_numero(jogador?.nivel)}
                      </td>
                      <td className="text-center">
                        <span className={`font-orbitron font-bold whitespace-nowrap ${valorClasse(posicao)}`}>
                          {formatar_valor(valor, categoria.unidade)}
                        </span>
                      </td>
                    </>
                  )}

                  <td>
                    {href && (
                      <Link
                        href={href}
                        className="p-2 rounded-lg hover:bg-primary/20 transition-colors inline-flex"
                        aria-label={`Ver perfil de ${jogador?.nome}`}
                      >
                        <ExternalLink className="w-4 h-4 text-muted-foreground" />
                      </Link>
                    )}
                  </td>
                </tr>
              );
            })}

            {!linhas.length && (
              <tr>
                <td
                  colSpan={6}
                  className="p-6 text-center text-muted-foreground"
                >
                  {vazio}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RankingTable;
